import { z } from "zod";

import { annualIncomeSchema } from "./annualIncome";
import {
  EMAIL_REQUIRED_MESSAGE,
  EMAIL_MAX_LENGTH_MESSAGE,
  EMAIL_FORMAT_MESSAGE,
  createRequiredMessage,
  createSelectRequiredMessage,
  FAMILY_NAME_REQUIRED_MESSAGE,
  FIRST_NAME_REQUIRED_MESSAGE,
  FAMILY_NAME_KANA_REQUIRED_MESSAGE,
  FIRST_NAME_KANA_REQUIRED_MESSAGE,
  KANA_FORMAT_MESSAGE,
  PHONE_NUMBER_REQUIRED_MESSAGE,
  PHONE_NUMBER_FORMAT_MESSAGE,
  HALF_WIDTH_NUMBER_FORMAT_MESSAGE,
  NAME_MAX_LENGTH_MESSAGE,
  PRICE_LIMIT_MESSAGE,
  INTEGER_FORMAT_MESSAGE,
  NON_NEGATIVE_MESSAGE,
} from "./message";
import {
  EMAIL_REGEX,
  MAX_EMAIL_LENGTH,
  NAME_KANA_REGEX,
  PHONE_NUMBER_REGEX,
  HALF_WIDTH_NUMBER_REGEX,
  MAX_NAME_LENGTH,
  isWithinIntLimit,
  isInteger,
  isNonNegative,
} from "./patterns";

export const updateEmailSchema = z.object({
  email: z
    .string()
    .min(1, EMAIL_REQUIRED_MESSAGE)
    .max(MAX_EMAIL_LENGTH, EMAIL_MAX_LENGTH_MESSAGE)
    .regex(EMAIL_REGEX, EMAIL_FORMAT_MESSAGE),
});

const JOB_INDUSTRY_REQUIRED_CATEGORIES = ["会社員（上場）", "会社員（非上場）", "会社員（外資系企業）"];

export const basicInfoSchema = z
  .object({
    consideration_period: z.string().nullable().optional(),
    past_meeting_count: z.string().nullable().optional(),
    considering_property: z.string().optional(),
    approved_financial_institutions: z.string().optional(),
    other_request_text: z.string().optional(),
    family_name: z.string().min(1, FAMILY_NAME_REQUIRED_MESSAGE).max(MAX_NAME_LENGTH, NAME_MAX_LENGTH_MESSAGE),
    first_name: z.string().min(1, FIRST_NAME_REQUIRED_MESSAGE).max(MAX_NAME_LENGTH, NAME_MAX_LENGTH_MESSAGE),
    family_name_kana: z
      .string()
      .min(1, FAMILY_NAME_KANA_REQUIRED_MESSAGE)
      .max(MAX_NAME_LENGTH, NAME_MAX_LENGTH_MESSAGE)
      .regex(NAME_KANA_REGEX, KANA_FORMAT_MESSAGE),
    first_name_kana: z
      .string()
      .min(1, FIRST_NAME_KANA_REQUIRED_MESSAGE)
      .max(MAX_NAME_LENGTH, NAME_MAX_LENGTH_MESSAGE)
      .regex(NAME_KANA_REGEX, KANA_FORMAT_MESSAGE),
    birth_year: z.number().min(1, createRequiredMessage("年")),
    birth_month: z.number().min(1, createRequiredMessage("月")),
    phone_number: z
      .string()
      .min(1, PHONE_NUMBER_REQUIRED_MESSAGE)
      .regex(PHONE_NUMBER_REGEX, PHONE_NUMBER_FORMAT_MESSAGE),
    phone_number_auth_code: z.string().optional(),
    address_prefecture: z.string().min(1, createSelectRequiredMessage("お住まいの地域（都道府県）")),
    job_category: z.string().min(1, createSelectRequiredMessage("ご職業")),
    job_industry: z.string().optional(),
    job_continuous_period: z.string().min(1, createSelectRequiredMessage("勤続年数")),
    annual_income: annualIncomeSchema,

    financial_asset: z.string().min(1, createSelectRequiredMessage("金融資産（預金・株式等）")),
    holding_property_count: z
      .string()
      .min(1, createSelectRequiredMessage("投資用不動産保有数"))
      .regex(HALF_WIDTH_NUMBER_REGEX, HALF_WIDTH_NUMBER_FORMAT_MESSAGE),
    real_estate_loan_amount: z
      .string()
      .refine((val) => !val || isInteger(val), { message: INTEGER_FORMAT_MESSAGE })
      .refine((val) => !val || isWithinIntLimit(val), { message: PRICE_LIMIT_MESSAGE })
      .refine((val) => !val || isNonNegative(val), { message: NON_NEGATIVE_MESSAGE }),
    current_residence_type_id: z
      .number()
      .nullable()
      .refine((val) => val, {
        message: createSelectRequiredMessage("お住まいの家"),
      }),
    home_loan_amount: z
      .string()
      .optional()
      .refine((val) => !val || isInteger(val), { message: INTEGER_FORMAT_MESSAGE })
      .refine((val) => !val || isWithinIntLimit(val), { message: PRICE_LIMIT_MESSAGE })
      .refine((val) => !val || isNonNegative(val), { message: NON_NEGATIVE_MESSAGE }),
    monthly_rent_payment_amount: z.preprocess(
      (val) => (typeof val === "number" ? String(val) : val),
      z
        .string()
        .optional()
        .refine((val) => !val || isInteger(val), { message: INTEGER_FORMAT_MESSAGE })
        .refine((val) => !val || isWithinIntLimit(val), { message: PRICE_LIMIT_MESSAGE })
        .refine((val) => !val || isNonNegative(val), { message: NON_NEGATIVE_MESSAGE })
    ),
    interest_tag_ids: z.array(z.number()).optional(),
    interested_property_type_ids: z.array(z.number()).min(1, createSelectRequiredMessage("最低1つの項目")),
  })
  .superRefine((data, ctx) => {
    // 業種（特定のjob_categoryの場合に必須）
    if (JOB_INDUSTRY_REQUIRED_CATEGORIES.includes(data.job_category) && !data.job_industry) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["job_industry"],
        message: "業種を選択してください。",
      });
    }

    // 投資用不動産ローン（保有数 > 0の場合に必須）
    if (Number(data.holding_property_count) > 0 && !data.real_estate_loan_amount) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["real_estate_loan_amount"],
        message: "投資用不動産ローン合計を選択してください。",
      });
    }

    // お住まいの家（賃貸）の場合に家賃を必須化
    if (Number(data.current_residence_type_id) === 2 && !data.monthly_rent_payment_amount) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["monthly_rent_payment_amount"],
        message: createRequiredMessage("家賃"),
      });
    }

    // お住まいの家（持ち家）の場合に住宅ローン残債を必須化
    if (Number(data.current_residence_type_id) === 1 && !data.home_loan_amount) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["home_loan_amount"],
        message: createRequiredMessage("住宅ローン残債"),
      });
    }
  });

// 型定義
export type UpdateEmailFormData = z.infer<typeof updateEmailSchema>;
export type BasicInfoFormData = z.infer<typeof basicInfoSchema>;
